import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../api';

interface GiftData {
    giftId: string;
    title: string;
    description?: string;
    backgroundType: string;
    status: string;
    items: Array<{
        title: string;
        imageUrl: string;
        productUrl?: string;
        requestSize: boolean;
        requestColor: boolean;
        requestNote: boolean;
    }>;
    chosenItemIndex?: number;
    createdAt: string;
    branding?: {
        logoUrl: string;
        companyName: string;
        primaryColor: string;
    };
}

export default function Choose() {
    const { giftId } = useParams<{ giftId: string }>();
    const navigate = useNavigate();
    const [gift, setGift] = useState<GiftData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [step, setStep] = useState<'select' | 'options' | 'shipping'>('select');

    // Options
    const [optSize, setOptSize] = useState('');
    const [optColor, setOptColor] = useState('');
    const [optNote, setOptNote] = useState('');

    // Shipping
    const [fullName, setFullName] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!giftId) return;
        api
            .getGift(giftId)
            .then((data) => {
                setGift(data);
                if (data.branding?.primaryColor) {
                    const root = document.documentElement;
                    root.style.setProperty('--color-primary', data.branding.primaryColor);
                    root.style.setProperty(
                        '--gradient-primary',
                        `linear-gradient(135deg, ${data.branding.primaryColor} 0%, rgba(255,255,255,0.2) 100%)`
                    );
                }
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));

        return () => {
            const root = document.documentElement;
            root.style.removeProperty('--color-primary');
            root.style.removeProperty('--gradient-primary');
        };
    }, [giftId]);

    const selectedItem = selectedIndex !== null ? gift?.items[selectedIndex] : null;
    const needsOptions = selectedItem && (selectedItem.requestSize || selectedItem.requestColor || selectedItem.requestNote);

    const handleSelect = (index: number) => {
        if (gift?.status !== 'OPEN') return;
        setSelectedIndex(index);
    };

    const handleContinue = () => {
        if (selectedIndex === null) return;
        if (needsOptions && step === 'select') {
            setStep('options');
        } else {
            setStep('shipping');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!giftId || selectedIndex === null) return;
        setSubmitting(true);
        setError('');

        try {
            await api.chooseGift(giftId, {
                itemIndex: selectedIndex,
                options: needsOptions
                    ? {
                        size: optSize || undefined,
                        color: optColor || undefined,
                        note: optNote || undefined,
                    }
                    : undefined,
                shipping: { fullName, street, city, zip, phone, email },
            });
            navigate('/success', {
                state: {
                    chosenItem: gift?.items[selectedIndex]?.title,
                    branding: gift?.branding
                },
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Nie udało się zapisać wyboru');
        } finally {
            setSubmitting(false);
        }
    };

    const renderHeader = () => {
        if (gift?.branding) {
            return (
                <header className="header">
                    <div className="container header-inner" style={{ justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <img
                                src={gift.branding.logoUrl}
                                alt={gift.branding.companyName}
                                style={{ height: '40px', objectFit: 'contain', borderRadius: '4px' }}
                            />
                            <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>{gift.branding.companyName}</span>
                        </div>
                    </div>
                </header>
            );
        }
        return <Header />;
    };

    if (loading) {
        return (
            <div className="page">
                {renderHeader()}
                <div className="loading-overlay">
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (error && !gift) {
        return (
            <div className="page">
                {renderHeader()}
                <div className="success-container">
                    <div className="success-icon">😕</div>
                    <h2>Nie znaleziono listy</h2>
                    <p className="text-muted mt-md">{error}</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (!gift) return null;

    if (gift.status === 'CHOSEN') {
        return (
            <div className="page">
                {renderHeader()}
                <div className="success-container">
                    <div className="success-icon">✅</div>
                    <h2>Prezent już został wybrany</h2>
                    <p className="text-muted mt-md">
                        Ktoś już wybrał prezent z tej listy. Dziękujemy za zainteresowanie!
                    </p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="page">
            {renderHeader()}
            <div className="container" style={{ padding: '48px 24px' }}>
                <div className="text-center mb-lg">
                    <h1>{gift.title}</h1>
                    {gift.description && (
                        <p className="text-muted mt-sm" style={{ maxWidth: 600, margin: '8px auto 0' }}>
                            {gift.description}
                        </p>
                    )}
                </div>

                {step === 'select' && (
                    <div className="fade-in">
                        <p className="text-center text-muted mb-lg">
                            Wybierz jeden prezent z poniższej listy 🎁
                        </p>
                        <div className="items-grid">
                            {gift.items.map((item, index) => (
                                <div
                                    key={index}
                                    className={`card item-card ${selectedIndex === index ? 'selected' : ''}`}
                                    onClick={() => handleSelect(index)}
                                >
                                    <img src={item.imageUrl} alt={item.title} loading="lazy" />
                                    <div className="item-card-body">
                                        <h3 style={{ fontSize: '1rem' }}>{item.title}</h3>
                                        {item.productUrl && (
                                            <a
                                                href={item.productUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ fontSize: '0.8rem' }}
                                            >
                                                Zobacz produkt →
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="text-center mt-xl">
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={handleContinue}
                                disabled={selectedIndex === null}
                            >
                                Dalej →
                            </button>
                        </div>
                    </div>
                )}

                {step === 'options' && selectedItem && (
                    <div className="fade-in" style={{ maxWidth: 500, margin: '0 auto' }}>
                        <div className="glass" style={{ padding: '32px' }}>
                            <h3 style={{ marginBottom: '20px' }}>🎨 Opcje dla: {selectedItem.title}</h3>
                            <div className="flex flex-col gap-md">
                                {selectedItem.requestSize && (
                                    <div className="form-group">
                                        <label className="form-label">Rozmiar</label>
                                        <input
                                            className="form-input"
                                            value={optSize}
                                            onChange={(e) => setOptSize(e.target.value)}
                                            placeholder="np. M, L, 42..."
                                        />
                                    </div>
                                )}
                                {selectedItem.requestColor && (
                                    <div className="form-group">
                                        <label className="form-label">Kolor</label>
                                        <input
                                            className="form-input"
                                            value={optColor}
                                            onChange={(e) => setOptColor(e.target.value)}
                                            placeholder="np. Czarny, Niebieski..."
                                        />
                                    </div>
                                )}
                                {selectedItem.requestNote && (
                                    <div className="form-group">
                                        <label className="form-label">Notatka</label>
                                        <textarea
                                            className="form-textarea"
                                            value={optNote}
                                            onChange={(e) => setOptNote(e.target.value)}
                                            placeholder="Dodatkowe uwagi..."
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="text-center mt-lg" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                <button className="btn btn-ghost" onClick={() => setStep('select')}>
                                    ← Wróć
                                </button>
                                <button className="btn btn-primary" onClick={() => setStep('shipping')}>
                                    Dalej →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'shipping' && (
                    <div className="fade-in">
                        <form className="shipping-form glass" style={{ padding: '32px' }} onSubmit={handleSubmit}>
                            <h3>📦 Dane do wysyłki</h3>
                            <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                Twoje dane nie będą przechowywane — zostaną wysłane wyłącznie do osoby, która stworzyła listę.
                            </p>

                            <div className="form-group">
                                <label className="form-label">Imię i nazwisko *</label>
                                <input
                                    className="form-input"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Jan Kowalski"
                                    required
                                    minLength={2}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Ulica i numer *</label>
                                <input
                                    className="form-input"
                                    value={street}
                                    onChange={(e) => setStreet(e.target.value)}
                                    placeholder="ul. Marszałkowska 1/2"
                                    required
                                    minLength={3}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Miasto *</label>
                                    <input
                                        className="form-input"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        placeholder="Warszawa"
                                        required
                                        minLength={2}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Kod pocztowy *</label>
                                    <input
                                        className="form-input"
                                        value={zip}
                                        onChange={(e) => setZip(e.target.value)}
                                        placeholder="00-001"
                                        required
                                        minLength={3}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Telefon *</label>
                                <input
                                    className="form-input"
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+48 123 456 789"
                                    required
                                    minLength={7}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Email *</label>
                                <input
                                    className="form-input"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="jan@email.com"
                                    required
                                />
                            </div>

                            {error && (
                                <div style={{ color: 'var(--color-error)', textAlign: 'center' }}>⚠️ {error}</div>
                            )}

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => setStep(needsOptions ? 'options' : 'select')}
                                >
                                    ← Wróć
                                </button>
                                <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <span className="spinner" /> Wysyłanie...
                                        </>
                                    ) : (
                                        '🎁 Zatwierdź wybór'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
